import { useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "wouter";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Field, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useT();
  const [, navigate] = useLocation();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("login.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm px-4 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("login.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label={t("auth.email")}>
              <TextInput type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label={t("auth.password")}>
              <TextInput
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? t("login.busy") : t("common.logIn")}
            </Button>
          </form>
          <p className="mt-4 text-sm">
            <Link href="/forgot" className="font-medium text-primary transition-colors hover:underline">
              {t("login.forgot")}
            </Link>
          </p>
          <p className="mt-6 text-sm text-muted-foreground">
            {t("login.noAccount")}{" "}
            <Link
              href={`/register?next=${encodeURIComponent(next)}`}
              className="font-medium text-primary transition-colors hover:underline"
            >
              {t("common.register")}
            </Link>
          </p>
        </CardContent>
      </Card>
      <p className="mt-4 text-center text-xs text-muted-foreground">{t("login.demo")}</p>
    </div>
  );
}
